/**
 * The API client. One place that knows the credential is a cookie the browser sends by
 * itself, that a 401 means sign in again and a 403 means this role may not do that, and
 * that an error body is `{ "error": "..." }` — so no screen has to reimplement any of it.
 */
import type { Me } from "./types";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** No usable credential. The console must send the user to sign in. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /**
   * Authenticated, but this role may not do that. Do NOT sign the user out — the old
   * `isAuth` returned true for both, which under roles logs a viewer out for clicking a
   * member-only control.
   */
  get isForbidden(): boolean {
    return this.status === 403;
  }
}

/**
 * `failure()` is the single funnel every response passes through — get, post, del,
 * upload, download and imageObjectUrl all call it — which makes it the one seam where a
 * 401 can be observed globally. A TanStack `QueryCache.onError` would miss download,
 * upload and imageObjectUrl, which are not queries.
 */
let onUnauthenticated: (() => void) | null = null;

export function setUnauthenticatedHandler(handler: (() => void) | null): void {
  onUnauthenticated = handler;
}

async function failure(response: Response): Promise<ApiError> {
  let message = `${response.status} ${response.statusText}`;
  try {
    const body = await response.json();
    if (typeof body?.error === "string") message = body.error;
  } catch {
    /* a non-JSON error body is still an error; the status line has to do */
  }
  const error = new ApiError(response.status, message);
  if (error.isUnauthenticated) onUnauthenticated?.();
  return error;
}

/**
 * The cookie already rides on the default `"same-origin"` in both environments —
 * production serves the console from the same binary via LRD_CONSOLE_DIR, and dev proxies
 * /v1 through Vite — so `include` is explicitness, not the mechanism.
 */
const CREDENTIALS: RequestCredentials = "include";

export async function get<T>(path: string): Promise<T> {
  const response = await fetch(path, { credentials: CREDENTIALS });
  if (!response.ok) throw await failure(response);
  return (await response.json()) as T;
}

export async function post<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(path, {
    method: "POST",
    credentials: CREDENTIALS,
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw await failure(response);
  return (await response.json()) as T;
}

export async function patch<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(path, {
    method: "PATCH",
    credentials: CREDENTIALS,
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw await failure(response);
  return (await response.json()) as T;
}

// Beside patch, which already has this exact shape (identity plan) — put is the one
// verb missing from it. CREDENTIALS is that plan's `const CREDENTIALS: RequestCredentials
// = "include"`, already in scope in this file; there is no headers() to spread in.
export async function put<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(path, {
    method: "PUT",
    credentials: CREDENTIALS,
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw await failure(response);
  return (await response.json()) as T;
}

export async function del(path: string): Promise<void> {
  const response = await fetch(path, { method: "DELETE", credentials: CREDENTIALS });
  if (!response.ok) throw await failure(response);
}

/**
 * Uploads go as multipart, one file per part, exactly as `POST /v1/documents` expects.
 *
 * XHR rather than fetch, and only here: fetch cannot report how many bytes of a request
 * body have gone out, so a drop that fits in one batch showed a bar pinned at 0 for the
 * whole upload and then jumped to done. `onProgress` gets a 0..1 fraction of the bytes
 * this request has sent. Errors still go through `failure()` — the response is rebuilt so
 * a 401 reaches the global handler exactly as it does on every other call.
 */
export async function upload<T>(
  path: string,
  files: File[],
  onProgress?: (fraction: number) => void,
): Promise<T> {
  const form = new FormData();
  for (const file of files) form.append("file", file, file.name);

  const xhr = new XMLHttpRequest();
  const body = await new Promise<{ status: number; statusText: string; text: string }>(
    (resolve, reject) => {
      xhr.open("POST", path);
      xhr.withCredentials = true;
      // `lengthComputable` is false for a body the browser cannot size up front; there is
      // nothing to report then, so leave the bar where the caller last put it.
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable && event.total > 0) onProgress?.(event.loaded / event.total);
      });
      xhr.addEventListener("load", () =>
        resolve({ status: xhr.status, statusText: xhr.statusText, text: xhr.responseText }),
      );
      xhr.addEventListener("error", () => reject(new ApiError(0, "upload failed: network error")));
      xhr.addEventListener("abort", () => reject(new ApiError(0, "upload cancelled")));
      xhr.send(form);
    },
  );

  if (body.status < 200 || body.status >= 300) {
    throw await failure(new Response(body.text, { status: body.status, statusText: body.statusText }));
  }
  return JSON.parse(body.text) as T;
}

/** A blob download that keeps the server's filename — used for the workbook. */
export async function download(path: string, fallbackName: string): Promise<void> {
  const response = await fetch(path, { credentials: CREDENTIALS });
  if (!response.ok) throw await failure(response);
  const disposition = response.headers.get("content-disposition") ?? "";
  const named = /filename="?([^";]+)"?/.exec(disposition);
  const url = URL.createObjectURL(await response.blob());
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = named?.[1] ?? fallbackName;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/** Who the caller is. The natural probe for the session gate. */
export async function me(): Promise<Me> {
  return get<Me>("/v1/me");
}

/**
 * Stays, even though a cookie would let `<img src="/v1/…/image">` work directly: this is
 * the only place that can observe the 409 "no printed page" reason `Crop.tsx` renders, and
 * turning three authenticated blob paths into directly linkable URLs that render customer
 * document pixels deserves its own decision rather than arriving as a side effect.
 */
export async function imageObjectUrl(path: string, signal?: AbortSignal): Promise<string> {
  const response = await fetch(path, { credentials: CREDENTIALS, signal });
  if (!response.ok) throw await failure(response);
  return URL.createObjectURL(await response.blob());
}
